import React, { useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Grow,
  InputAdornment,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { Chrome, color as parseColor, type ColorResult } from '@uiw/react-color';

type Props = {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  dataTestId?: string;
  disabled?: boolean;
};

const normalizeHex = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  return null;
};

export const OnboardingColorField = ({
  label,
  value,
  onChange,
  dataTestId,
  disabled = false,
}: Props): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = useState(value);
  const colorResult = parseColor(value || '#FFFFFF');

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const applyColor = (next: ColorResult) => {
    const hex = next.hex.toUpperCase();
    setInputValue(hex);
    onChange(hex);
  };

  return (
    <Box data-testid={dataTestId}>
      <Typography variant="medium" fontWeight="semibold" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <TextField
        fullWidth
        size="small"
        disabled={disabled}
        value={inputValue}
        onChange={(event) => {
          const next = event.target.value;
          setInputValue(next);
          const normalized = normalizeHex(next);
          if (normalized) {
            onChange(normalized);
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box
                component="button"
                type="button"
                aria-label={`${label} color picker`}
                onClick={(event) => {
                  if (!disabled) {
                    setAnchorEl(event.currentTarget);
                  }
                }}
                disabled={disabled}
                sx={{
                  p: 0,
                  width: 24,
                  height: 24,
                  border: '1px solid',
                  borderColor: 'border.main',
                  borderRadius: '4px',
                  backgroundColor: colorResult.hex,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                }}
              />
            </InputAdornment>
          ),
        }}
      />
      <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom-start" transition>
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Grow {...TransitionProps} style={{ transformOrigin: '0 0 0' }} timeout={200}>
              <Chrome color={colorResult.hsva} onChange={applyColor} />
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
};
